import axios from "axios";
import bbox from "@turf/bbox";
import intersect from "@turf/intersect";
import calculate_area from "@turf/area";
import geojson_geometries_lookup from "geojson-geometries-lookup";

import grid_functions from "../validation/grid_functions";
import { bun_server } from "./baseUrl";

const { get_grids_in_bounding_box_and_grouping } = grid_functions;

const grouping_number = 10;

const keys_float = [
  "PERTUMBUHAN PENDUDUK TAHUN 2020 (%)",
  "PERTUMBUHAN PENDUDUK TAHUN 2021 (%)",
  "PERTUMBUHAN PENDUDUK TAHUN 2022 (%)",
  "PERTUMBUHAN PENDUDUK TAHUN 2023 (%)",
  "PERTUMBUHAN PENDUDUK TAHUN 2024 (%)",
];

const keys_interger = [
  "JUMLAH PENDUDUK 2020",
  "JUMLAH PENDUDUK 2021",
  "JUMLAH PENDUDUK 2022",
  "JUMLAH PENDUDUK 2023",
  "JUMLAH PENDUDUK 2024",
  "KEPADATAN PENDUDUK 2020",
  "KEPADATAN PENDUDUK 2021",
  "KEPADATAN PENDUDUK 2022",
  "KEPADATAN PENDUDUK 2023",
  "KEPADATAN PENDUDUK 2024",
  "LAHIR TAHUN 2020",
  "LAHIR TAHUN 2021",
  "LAHIR TAHUN 2022",
  "LAHIR TAHUN 2023",
  "LAHIR TAHUN 2024",
  "ISLAM",
  "KRISTEN",
  "KATHOLIK",
  "HINDU",
  "BUDDHA",
  "KONGHUCU",
  "KEPERCAYAAN TERHADAP TUHAN YME",
  "LAKI-LAKI",
  "PEREMPUAN",
  "BELUM KAWIN",
  "KAWIN",
  "CERAI HIDUP",
  "CERAI MATI",
  "USIA 0-4",
  "USIA 5-9",
  "USIA 10-14",
  "USIA 15-19",
  "USIA 20-24",
  "USIA 25-29",
  "USIA 30-34",
  "USIA 35-39",
  "USIA 40-44",
  "USIA 45-49",
  "USIA 50-54",
  "USIA 55-59",
  "USIA 60-64",
  "USIA 65-69",
  "USIA 70-74",
  "USIA 75 TAHUN KE ATAS",
  "USIA PENDIDIKAN 3-4 TAHUN",
  "USIA PENDIDIKAN 5 TAHUN",
  "USIA PENDIDIKAN 12-14 TAHUN",
  "USIA PENDIDIKAN 15-17 TAHUN",
  "USIA PENDIDIKAN 18-22 TAHUN",
  "TIDAK/BELUM SEKOLAH",
  "BELUM TAMAT SD",
  "TAMAT SD",
  "SLTP",
  "SLTA",
  "D1 DAN D2",
  "D3",
  "S1",
  "S2",
  "S3",
  "GOLONGAN DARAH A",
  "GOLONGAN DARAH A+",
  "GOLONGAN DARAH A-",
  "GOLONGAN DARAH B",
  "GOLONGAN DARAH B+",
  "GOLONGAN DARAH B-",
  "GOLONGAN DARAH O",
  "GOLONGAN DARAH O+",
  "GOLONGAN DARAH O-",
  "GOLONGAN DARAH AB",
  "GOLONGAN DARAH AB+",
  "GOLONGAN DARAH AB-",
  "GOLONGAN DARAH TIDAK DIKETAHUI",
  "BELUM/TIDAK BEKERJA",
  "NELAYAN",
  "PELAJAR DAN MAHASISWA",
  "PENSIUNAN",
  "PERDAGANGAN",
  "MENGURUS RUMAH TANGGA",
  "WIRASWASTA",
  "GURU",
  "PERAWAT",
  "PENGACARA",
  "PEKERJAAN LAINNYA",
  "LUAS WILAYAH (KM²)",
  "JUMLAH PENDUDUK 2024",
  "JUMLAH KK",
  "PERPINDAHAN PENDUDUK",
  "JUMLAH MENINGGAL",
  "PERUBAHAN DATA",
  "JUMLAH WAJIB KTP",
  "JUMLAH REKAM WAJIB KTP",
];

/**
 * @route : set_demography
 * @endpoint : /sini_v2/set_demography
 * @methode :
 * @params : request_id, geojson_polygon_isochrone, lat, long
 * @body : 
      request_id: request_id,
      demography_list: demography_list_id,
      demography_intersect_list,
      demography_properties,
 */
export const set_demography = (params) => async (dispatch) => {
  try {
    dispatch({
      type: "set_loading_action",
      payload: "set_demography",
    });
    const config = {
      headers: {
        accesstoken: localStorage.token_mapid,
      },
    };

    const { request_id, geojson_polygon_isochrone, lat, long } = params;

    let [min_long_larger, min_lat_larger, max_long_larger, max_lat_larger] =
      bbox(geojson_polygon_isochrone);
    const delta_larger = 0.02;
    min_long_larger -= delta_larger;
    min_lat_larger -= delta_larger;
    max_long_larger += delta_larger;
    max_lat_larger += delta_larger;

    let demography_list = [];

    const grid_id_list = get_grids_in_bounding_box_and_grouping({
      min_lat: min_lat_larger,
      min_long: min_long_larger,
      max_lat: max_lat_larger,
      max_long: max_long_larger,
      grouping_number,
    });

    const delay_milisecond_constant = 0;
    const delay_promise = () =>
      new Promise((res) => setTimeout(res, delay_milisecond_constant));
    const parent_function = () => {
      return grid_id_list.reduce(
        (last_promise, object) =>
          last_promise.then((result_sum) =>
            child_function(object).then((result_current) => [
              ...result_sum,
              result_current,
            ])
          ),
        Promise.resolve([])
      );
    };
    const child_function = async (grid_list) => {
      return delay_promise().then(() => {
        const core_function = async () => {
          try {
            const body = { grid_list };
            const res = await axios.post(
              bun_server + "/sini_v2/demography_list",
              body,
              config
            );
            const list_temp = res?.data || [];
            if (list_temp.length > 0) {
              demography_list = [...demography_list, ...list_temp];
            }
          } catch (error) {}
        };
        return core_function();
      });
    };

    await parent_function().then(async () => {
      try {
        /*PROSES INTERSECT MULAI*/
        const geometry_point = {
          coordinates: [long, lat],
          type: "Point",
        };
        const geojson_demography = {
          type: "FeatureCollection",
          features: [],
        };
        demography_list.forEach((item) => {
          if (item.geometry.type === "MultiPolygon") {
            item.geometry.type = "Polygon";
            item.geometry.coordinates = item.geometry.coordinates[0];
          }
          if (item.geometry.type === "Polygon") {
            geojson_demography.features.push(item);
          }
        });
        const glookup = new geojson_geometries_lookup(geojson_demography);
        const geojson_demography_text = glookup.getContainers(geometry_point);
        const properties_text =
          geojson_demography_text?.features?.[0]?.properties || {};

        const feature_isochrone = geojson_polygon_isochrone?.features?.[0];
        let demography_intersect_list = [];
        demography_list.forEach((feature_demography) => {
          const geojson_process = {
            type: "FeatureCollection",
            features: [feature_demography, feature_isochrone],
          };
          let feature_result = intersect(geojson_process);
          if (feature_result !== null) {
            const area_demo_m2 = calculate_area(feature_demography);
            const area_result_m2 = calculate_area(feature_result);
            const proportional = (area_result_m2 / area_demo_m2).toFixed(2);
            feature_result.properties.area_demo_m2 = area_demo_m2;
            feature_result.properties.area_result_m2 = area_result_m2;
            feature_result.properties.proportional = proportional;
            const properties = feature_demography?.properties || {};
            keys_interger.forEach((item) => {
              const raw_value = properties?.[item] || 0;
              feature_result.properties[item] = proportional * raw_value;
            });
            keys_float.forEach((item) => {
              const raw_value = properties?.[item] || 0;
              feature_result.properties[item] = proportional * raw_value;
            });

            feature_result.properties = {
              ...feature_demography.properties,
              ...feature_result.properties,
            };
            demography_intersect_list.push(feature_result);
          }
        });
        let properties_number = {};

        //summation
        demography_intersect_list.forEach((feature) => {
          keys_interger.forEach((item) => {
            const init_number = properties_number?.[item] || 0;
            const add_number = feature?.properties?.[item] || 0;
            properties_number[item] = init_number + add_number;
          });
          keys_float.forEach((item) => {
            const init_number = properties_number?.[item] || 0;
            const add_number = feature?.properties?.[item] || 0;
            properties_number[item] = init_number + add_number;
          });
        });

        //pembualatan
        keys_interger.forEach((item) => {
          const init_number = properties_number?.[item] || 0;
          properties_number[item] = Math.round(init_number);
        });
        keys_float.forEach((item) => {
          const init_number = properties_number?.[item] || 0;
          properties_number[item] = parseFloat(init_number.toFixed(1));
        });

        const demography_properties = {
          ...properties_text,
          ...properties_number,
        };
        /*PROSES INTERSECT SELESAI*/

        if (
          demography_properties?.["KABKOT"] &&
          demography_properties?.["PROVINSI"]
        ) {
          const KABKOT = demography_properties?.["KABKOT"];
          const PROVINSI = demography_properties?.["PROVINSI"];
          const res = await axios.get(
            bun_server +
              `/sini_v2/people_spending_by_city?KABKOT=${KABKOT}&PROVINSI=${PROVINSI}`,
            config
          );
          const people_spending = res?.data || {};
          dispatch({
            type: "edit_inside_array_sini",
            payload: {
              key: "ai_parent_detail_list",
              id_key_parent: "request_id",
              id_value_parent: request_id,
              id_key_child: "people_spending",
              value_child: people_spending,
            },
          });

          let people_spending_id;

          if (people_spending?._id) {
            people_spending_id = people_spending._id;
          }

          const demography_list_id = demography_list.map((item) => item._id);
          let body = {
            request_id: request_id,
            demography_list: demography_list_id,
            demography_intersect_list,
            demography_properties,
          };
          if (people_spending_id) {
            body = { ...body, people_spending: people_spending_id };
          }
          await axios.put(bun_server + "/sini_v2/set_demography", body, config);

          dispatch({
            type: "edit_inside_array_sini",
            payload: {
              key: "ai_parent_detail_list",
              id_key_parent: "request_id",
              id_value_parent: request_id,
              id_key_child: "demography_list",
              value_child: demography_list,
            },
          });
          dispatch({
            type: "edit_inside_array_sini",
            payload: {
              key: "ai_parent_detail_list",
              id_key_parent: "request_id",
              id_value_parent: request_id,
              id_key_child: "demography_intersect_list",
              value_child: demography_intersect_list,
            },
          });
          dispatch({
            type: "edit_inside_array_sini",
            payload: {
              key: "ai_parent_detail_list",
              id_key_parent: "request_id",
              id_value_parent: request_id,
              id_key_child: "demography_properties",
              value_child: demography_properties,
            },
          });
          dispatch({
            type: "clear_loading_action",
            payload: "set_demography",
          });
        }
      } catch (error) {}
    });
  } catch (error) {
    dispatch({
      type: "clear_loading_action",
      payload: "set_demography",
    });
  }
};
